import { useState } from 'react';
import { FiX, FiCloud, FiCheck, FiAlertCircle } from 'react-icons/fi';

interface SyncSettingsProps {
  onClose: () => void;
}

const SyncSettings = ({ onClose }: SyncSettingsProps) => {
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);

  // Google Drive 동기화 (데모 버전)
  const handleSync = async () => {
    setSyncing(true);

    // 실제 구현 시 Google Drive API 호출
    await new Promise(resolve => setTimeout(resolve, 2000));

    setLastSync(new Date());
    setSyncing(false);
    alert('동기화 완료! (데모 버전)\n\n실제 사용을 위해서는:\n1. Google Cloud Console에서 프로젝트 생성\n2. Google Drive API 활성화\n3. OAuth 2.0 클라이언트 ID 생성\n4. 환경 변수에 클라이언트 ID 설정');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiCloud size={24} />
            클라우드 동기화
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* 설명 */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-2">
            <FiAlertCircle className="text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-semibold mb-1">Google Drive 동기화 (데모)</p>
              <p className="text-xs">
                책 데이터, 하이라이트, 노트를 Google Drive에 자동 백업합니다.
                실제 사용을 위해서는 Google API 설정이 필요합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 동기화 설정 */}
        <div className="space-y-4 mb-6">
          {/* 동기화 활성화 */}
          <label className="flex items-center justify-between p-4 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                클라우드 동기화 활성화
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Google Drive에 자동으로 백업
              </div>
            </div>
            <input
              type="checkbox"
              checked={syncEnabled}
              onChange={(e) => setSyncEnabled(e.target.checked)}
              className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
            />
          </label>

          {/* 자동 동기화 */}
          {syncEnabled && (
            <label className="flex items-center justify-between p-4 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  자동 동기화
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  변경 사항 자동 업로드
                </div>
              </div>
              <input
                type="checkbox"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
              />
            </label>
          )}
        </div>

        {/* 마지막 동기화 시간 */}
        {lastSync && (
          <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
              <FiCheck size={16} />
              <span>
                마지막 동기화: {lastSync.toLocaleString('ko-KR')}
              </span>
            </div>
          </div>
        )}

        {/* 동기화 버튼 */}
        <button
          onClick={handleSync}
          disabled={!syncEnabled || syncing}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
        >
          {syncing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>동기화 중...</span>
            </>
          ) : (
            <>
              <FiCloud size={20} />
              <span>지금 동기화</span>
            </>
          )}
        </button>

        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          닫기
        </button>

        {/* 안내 */}
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            💡 <strong>개발자 안내:</strong> 실제 Google Drive 연동을 위해서는
            Google Cloud Console에서 API 키를 발급받아야 합니다.
            <a
              href="https://console.cloud.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline ml-1"
            >
              설정 가이드 보기 →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SyncSettings;
